import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, ShieldCheck } from 'lucide-react';
import logo from '../assets/logo.png';

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPw, setShowPw] = useState(false);
  
  const { updatePassword } = useAuth();
  const navigate = useNavigate();

  const handleReset = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await updatePassword(password);
      setSuccess(true);
      setTimeout(() => navigate('/'), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-black text-sm placeholder:text-gray-400 focus:outline-none focus:border-black transition-colors";

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
      <div className="max-w-md w-full bg-white border border-gray-100 rounded-3xl p-8 sm:p-12 shadow-2xl shadow-black/5">
        <div className="flex flex-col items-center mb-8">
          <img src={logo} alt="The Barber Craft" className="h-16 w-auto object-contain mb-6" />
          <h1 className="font-serif text-3xl text-black text-center">Reset Your Password</h1>
          <p className="text-black/40 text-sm mt-2 text-center uppercase tracking-widest font-bold">Secure Your Account</p>
        </div>

        {success ? (
          <div className="text-center space-y-4 py-4">
            <div className="w-16 h-16 bg-green-50 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldCheck size={32} />
            </div>
            <h2 className="text-xl font-semibold text-black">Password Updated!</h2>
            <p className="text-black/60 text-sm">Your password has been changed successfully. Redirecting you to home...</p>
          </div>
        ) : (
          <form onSubmit={handleReset} className="space-y-6">
            {error && (
              <div className="p-4 bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl">
                {error}
              </div>
            )}

            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-black/40 font-bold mb-2">New Password</label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} required value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Min 6 characters" className={`${inputClass} pr-12`} />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-3.5 text-gray-400 hover:text-gray-600">
                  {showPw ? <EyeOff size={18}/> : <Eye size={18}/>}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-black/40 font-bold mb-2">Confirm New Password</label>
              <input type={showPw ? 'text' : 'password'} required value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password" className={inputClass} />
            </div>

            <button type="submit" disabled={loading}
              className="w-full py-4 bg-black text-white text-xs font-bold uppercase tracking-[0.2em] rounded-xl hover:bg-gray-800 transition-all active:scale-95 disabled:opacity-50 mt-4">
              {loading ? 'Updating...' : 'Set New Password'}
            </button>
          </form>
        )}
      </div>
      
      <p className="mt-8 text-black/20 text-[10px] uppercase tracking-widest font-bold">
        © 2026 The Barber Craft · Quality Styles
      </p>
    </div>
  );
}
